import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchWithLocalCache } from "../lib/cache";

/* -------------------- responsive helper -------------------- */
function useIsMobile(breakpoint = 900) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

/* -------------------- columns -------------------- */
const columns = [
  { label: "ETF", key: "etf", type: "text" },
  { label: "Ticker", key: "symbol", type: "text" },
  { label: "Price", key: "price", type: "number" },
  { label: "Volume", key: "volume", type: "number" },
  { label: "52 Wk Low Chg", key: "wkLowChg", type: "percent" },
  { label: "RSI", key: "rsi", type: "number" },
  // { label: "52 Wk High Chg%", key: "wkHighChg", type: "percent" },
  { label: "Beta", key: "beta", type: "number" },
  { label: "Dividend Yield", key: "divYield", type: "percent" },
  { label: "Dividend Growth", key: "divGrowth", type: "percent" },
  { label: "Expense Ratio", key: "expense", type: "percent" },
  { label: "PE", key: "pe", type: "number" },
];

// ✅ Mobile: show only these columns
const mobileColumns = [
  { label: "Ticker", key: "symbol", type: "text" },
  { label: "Price", key: "price", type: "number" },
  { label: "Volume", key: "volume", type: "number" },
  { label: "52 Wk Low Chg", key: "wkLowChg", type: "percent" },
  { label: "RSI", key: "rsi", type: "number" },
];

// Map internal keys -> EXACT sheet header names
const keyMap = {
  etf: "ETF",
  symbol: "Symbol",
  price: "Price",
  wkLowChg: "52 Wk Low Change",
  rsi: "RSI",
  wkHighChg: "52 Week High Ch%",
  beta: "Beta",
  divYield: "Dividend Yield",
  divGrowth: "Dividend Growth",
  volume: "Volume",
  expense: "Expense Ratio",
  pe: "PE Ratio",
};

function formatNumberCompact(n) {
  if (n === null || n === undefined || n === "") return "";
  const num = Number(String(n).replace(/,/g, ""));
  if (!Number.isFinite(num)) return String(n);

  if (num >= 1e12) return (num / 1e12).toFixed(2) + "T";
  if (num >= 1e9) return (num / 1e9).toFixed(2) + "B";
  if (num >= 1e6) return (num / 1e6).toFixed(2) + "M";
  if (num >= 1e3) return (num / 1e3).toFixed(2) + "K";
  return num.toString();
}

// tolerates "0.12" => 12% and "12" => 12%
function formatPercent(v, decimals = 2) {
  if (v === null || v === undefined || v === "") return "";
  const num = Number(String(v).replace(/[% ,]/g, ""));
  if (!Number.isFinite(num)) return String(v);

  const asFraction = Math.abs(num) <= 1.5;
  const pct = asFraction ? num * 100 : num;
  return pct.toFixed(decimals) + "%";
}

function formatDecimal(v, decimals = 2) {
  if (v === null || v === undefined || v === "") return "";
  const num = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(num)) return String(v);
  return num.toFixed(decimals);
}

export default function ETFs() {
  const isMobile = useIsMobile(900);
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);

  const visibleColumns = useMemo(() => (isMobile ? mobileColumns : columns), [isMobile]);

  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ✅ spacing
  const TH_PAD = isMobile ? "5px 8px" : "6px 12px";
  const TD_PAD = isMobile ? "5px 8px" : "6px 12px";
  const FILTER_TH_PAD = isMobile ? "4px 8px" : "4px 12px";
  const HEADER_FONT = isMobile ? 10 : 11;
  const CELL_FONT = isMobile ? 12 : 13;
  const FILTER_FONT = isMobile ? 9 : 10;

  // ✅ freeze ticker width
  const TICKER_W = 92;

  // ✅ sticky header row heights (for filter-row offset)
  const HEADER_ROW_H = 34;

  const [filters, setFilters] = useState(() => {
    const init = {};
    columns.forEach((c) => {
      if (c.type === "text") init[c.key] = { contains: "" };
      else init[c.key] = { min: "", max: "" };
    });
    return init;
  });

  // ensure all keys exist (even if mobile hides some)
  useEffect(() => {
    setFilters((prev) => {
      const next = { ...prev };
      columns.forEach((c) => {
        if (!next[c.key]) next[c.key] = c.type === "text" ? { contains: "" } : { min: "", max: "" };
      });
      return next;
    });
  }, []);

  function setFilter(key, patch) {
    setFilters((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), ...patch },
    }));
  }

  function clearAllFilters() {
    const init = {};
    columns.forEach((c) => {
      if (c.type === "text") init[c.key] = { contains: "" };
      else init[c.key] = { min: "", max: "" };
    });
    setFilters(init);
    setQuery("");
  }

  const [sortKey, setSortKey] = useState("volume");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setError("");
        setLoading(true);

        const base = process.env.REACT_APP_SHEETS_API_URL;
        if (!base) throw new Error("Missing REACT_APP_SHEETS_API_URL (.env + restart)");
        const apiKey = process.env.REACT_APP_API_KEY;
        if (!apiKey) throw new Error("Missing REACT_APP_API_KEY (.env + restart)");

        const url = `${base}?type=etfs&view=list&key=${encodeURIComponent(apiKey)}`;
        const cacheKey = `etfs_list_v2`;

        const res = await fetchWithLocalCache(url, cacheKey, 60 * 60 * 1000);
        const payload = res?.data ?? res;
        if (payload?.error) throw new Error(payload.error);

        if (!cancelled) setItems(Array.isArray(payload) ? payload : []);
      } catch (e) {
        if (!cancelled) setError(String(e.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  function getCell(row, colKey) {
    const header = keyMap[colKey];
    return header ? row?.[header] : undefined;
  }

  function parseNumber(v) {
    if (v === null || v === undefined) return null;
    const s = String(v).trim();
    if (!s) return null;

    const cleaned = s.replace(/[$,%]/g, "").replace(/,/g, "");
    const m = cleaned.match(/^(-?\d+(\.\d+)?)([KMBT])?$/i);
    if (m) {
      const n = Number(m[1]);
      const suf = (m[3] || "").toUpperCase();
      const mult = suf === "K" ? 1e3 : suf === "M" ? 1e6 : suf === "B" ? 1e9 : suf === "T" ? 1e12 : 1;
      return Number.isFinite(n) ? n * mult : null;
    }

    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    return items.filter((row) => {
      if (q) {
        const ok = columns.some((c) => String(getCell(row, c.key) ?? "").toLowerCase().includes(q));
        if (!ok) return false;
      }

      for (const c of columns) {
        const f = filters[c.key];
        if (!f) continue;

        const raw = getCell(row, c.key);

        if (c.type === "text") {
          const needle = String(f.contains || "").trim().toLowerCase();
          if (needle) {
            const hay = String(raw ?? "").toLowerCase();
            if (!hay.includes(needle)) return false;
          }
          continue;
        }

        const minStr = String(f.min || "").trim();
        const maxStr = String(f.max || "").trim();
        if (!minStr && !maxStr) continue;

        const valNum = parseNumber(raw);
        if (valNum === null) return false;

        const minNum = minStr ? parseNumber(minStr) : null;
        const maxNum = maxStr ? parseNumber(maxStr) : null;

        if (minNum !== null && valNum < minNum) return false;
        if (maxNum !== null && valNum > maxNum) return false;
      }

      return true;
    });
  }, [items, query, filters]);

  const sorted = useMemo(() => {
    const effectiveSortKey =
      isMobile && !visibleColumns.some((c) => c.key === sortKey) ? "price" : sortKey;

    const col = columns.find((x) => x.key === effectiveSortKey);
    if (!col) return filtered;

    const dir = sortDir === "asc" ? 1 : -1;

    return [...filtered].sort((a, b) => {
      const av = getCell(a, effectiveSortKey);
      const bv = getCell(b, effectiveSortKey);

      const an = parseNumber(av);
      const bn = parseNumber(bv);

      if (an !== null && bn === null) return -1;
      if (an === null && bn !== null) return 1;

      if (an !== null && bn !== null) return (an - bn) * dir;

      const as = String(av ?? "").trim().toLowerCase();
      const bs = String(bv ?? "").trim().toLowerCase();

      const aEmpty = as === "";
      const bEmpty = bs === "";
      if (aEmpty && !bEmpty) return 1;
      if (!aEmpty && bEmpty) return -1;

      if (as < bs) return -1 * dir;
      if (as > bs) return 1 * dir;
      return 0;
    });
  }, [filtered, sortKey, sortDir, isMobile, visibleColumns]);

  function onHeaderClick(colKey) {
    if (sortKey === colKey) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortKey(colKey);
      if (["volume"].includes(colKey)) setSortDir("desc");
      else setSortDir("asc");
    }
  }

  function renderFilterInputs(col) {
    const f = filters[col.key] || {};

    if (col.type === "text") {
      return (
        <input
          value={f.contains || ""}
          onChange={(e) => setFilter(col.key, { contains: e.target.value })}
          placeholder="filter…"
          style={{
            width: "100%",
            padding: "4px 6px",
            fontSize: FILTER_FONT,
            border: "1px solid #eee",
            borderRadius: 8,
            outline: "none",
            boxSizing: "border-box",
          }}
        />
      );
    }

    // ✅ min stacked above max to save width
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <input
          value={f.min || ""}
          onChange={(e) => setFilter(col.key, { min: e.target.value })}
          placeholder="min"
          style={{
            width: "100%",
            padding: "4px 6px",
            fontSize: FILTER_FONT,
            border: "1px solid #eee",
            borderRadius: 8,
            outline: "none",
            boxSizing: "border-box",
          }}
        />
        <input
          value={f.max || ""}
          onChange={(e) => setFilter(col.key, { max: e.target.value })}
          placeholder="max"
          style={{
            width: "100%",
            padding: "4px 6px",
            fontSize: FILTER_FONT,
            border: "1px solid #eee",
            borderRadius: 8,
            outline: "none",
            boxSizing: "border-box",
          }}
        />
      </div>
    );
  }

  // ✅ freeze ticker (left sticky)
  const stickyLeftBase = {
    position: "sticky",
    left: 0,
    zIndex: 3,
    background: "#fff",
  };

  // ✅ freeze headers (top sticky) + filter row below it
  const stickyHeaderBase = {
    position: "sticky",
    top: 0,
    zIndex: 7,
    background: "#fff",
  };

  const stickyFilterBase = {
    position: "sticky",
    top: HEADER_ROW_H,
    zIndex: 6,
    background: "#fcfcfc",
  };

  return (
    <div style={{ maxWidth: 1400, margin: "0 auto", padding: isMobile ? 12 : 16, fontFamily: "system-ui" }}>
      {/* Top nav/header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0 }}>ETFs</h2>

        <div style={{ marginLeft: "auto", display: "flex", gap: 12, fontSize: 13, flexWrap: "wrap" }}>
          <Link to="/stocks" style={{ textDecoration: "none" }}>Stocks</Link>
          <Link to="/etfs" style={{ textDecoration: "none" }}>ETFs</Link>
          <Link to="/feed" style={{ textDecoration: "none" }}>Feed</Link>
        </div>
      </div>

      {/* Controls */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search (ticker, ETF name, etc.)"
          style={{
            width: isMobile ? "100%" : 420,
            padding: 10,
            minWidth: 0,
            fontSize: 14,
            border: "1px solid #eee",
            borderRadius: 10,
            outline: "none",
          }}
        />

        <button
          onClick={clearAllFilters}
          style={{
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid #eee",
            background: "#fafafa",
            cursor: "pointer",
          }}
          title="Clears search + all column filters"
        >
          Clear filters
        </button>

        {isMobile && (
          <button
            onClick={() => setShowFiltersMobile((v) => !v)}
            style={{
              padding: "10px 12px",
              borderRadius: 10,
              border: "1px solid #eee",
              background: "#fff",
              cursor: "pointer",
            }}
          >
            {showFiltersMobile ? "Hide filters" : "Show filters"}
          </button>
        )}

        <div style={{ color: "#666", fontSize: 13 }}>
          Showing {sorted.length} / {items.length}
        </div>
      </div>

      {loading && <div style={{ marginTop: 12 }}>Loading…</div>}
      {error && <div style={{ marginTop: 12, color: "crimson" }}>Error: {error}</div>}

      {!loading && !error && (
        <div
          style={{
            marginTop: 12,
            overflowX: "auto",
            overflowY: "auto",
            maxHeight: "75vh", // makes frozen headers useful
            border: "1px solid #eee",
            borderRadius: 12,
            WebkitOverflowScrolling: "touch",
          }}
        >
          <table
            style={{
              width: "100%",
              minWidth: isMobile ? 520 : 900,
              borderCollapse: "separate",
              borderSpacing: 0,
              tableLayout: "auto",
            }}
          >
            <thead>
              {/* Header labels (sticky) */}
              <tr>
                {visibleColumns.map((c) => {
                  const isTicker = c.key === "symbol";
                  const leftSticky = isTicker ? { ...stickyLeftBase, width: TICKER_W, minWidth: TICKER_W, maxWidth: TICKER_W } : {};

                  // if ticker header cell needs to be above everything
                  const zBoost = isTicker ? { zIndex: 10 } : {};

                  return (
                    <th
                      key={c.key}
                      onClick={() => onHeaderClick(c.key)}
                      style={{
                        padding: TH_PAD,
                        fontSize: HEADER_FONT,
                        fontWeight: 650,
                        color: "#666",
                        cursor: "pointer",
                        userSelect: "none",
                        borderBottom: "1px solid #eee",

                        // ✅ wrap only at spaces; never split a word
                        whiteSpace: "normal",
                        wordBreak: "keep-all",
                        overflowWrap: "normal",
                        lineHeight: 1.15,

                        ...stickyHeaderBase,
                        ...(isTicker ? { ...leftSticky, ...stickyHeaderBase, background: "#fff" } : {}),
                        ...zBoost,
                      }}
                      title="Click to sort"
                    >
                      {c.label}
                      {sortKey === c.key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                    </th>
                  );
                })}
              </tr>

              {/* Filters row (sticky, optional on mobile) */}
              {(!isMobile || showFiltersMobile) && (
                <tr>
                  {visibleColumns.map((c) => {
                    const isTicker = c.key === "symbol";
                    const leftSticky = isTicker ? { ...stickyLeftBase, width: TICKER_W, minWidth: TICKER_W, maxWidth: TICKER_W } : {};
                    const zBoost = isTicker ? { zIndex: 9 } : {};

                    return (
                      <th
                        key={c.key}
                        style={{
                          padding: FILTER_TH_PAD,
                          fontWeight: 400,
                          borderBottom: "1px solid #eee",
                          background: "#fcfcfc",
                          verticalAlign: "top",

                          ...stickyFilterBase,
                          ...(isTicker ? { ...leftSticky, ...stickyFilterBase, background: "#fcfcfc" } : {}),
                          ...zBoost,
                        }}
                      >
                        {renderFilterInputs(c)}
                      </th>
                    );
                  })}
                </tr>
              )}
            </thead>

            <tbody>
              {sorted.map((row, idx) => (
                <tr key={idx}>
                  {visibleColumns.map((c) => {
                    const isTicker = c.key === "symbol";
                    const leftSticky = isTicker ? { ...stickyLeftBase, width: TICKER_W, minWidth: TICKER_W, maxWidth: TICKER_W } : {};

                    const raw = getCell(row, c.key);
                    let display = raw;

                    if (c.key === "price") display = formatDecimal(raw, 2);
                    else if (c.key === "volume") display = formatNumberCompact(raw);
                    else if (["rsi", "beta", "pe"].includes(c.key)) display = formatDecimal(raw, 2);
                    else if (["wkLowChg", "wkHighChg", "divYield", "divGrowth", "expense"].includes(c.key))
                      display = formatPercent(raw, 2);

                    const text = String(display ?? "").trim();

                    return (
                      <td
                        key={c.key}
                        style={{
                          padding: TD_PAD,
                          fontSize: CELL_FONT,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          borderBottom: "1px solid #f3f3f3",
                          background: isTicker ? "#fff" : "transparent",
                          ...(isTicker ? leftSticky : {}),
                        }}
                        title={text}
                      >
                        {text}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
